import { when } from 'mobx';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Flight } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { ArchiveModal, toast } from '@feathr/components';
import CampaignSummary from '@feathr/extender/App/EventsPage/CampaignSummary';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { errorMessage } from '@feathr/hooks';

interface ICampaignDeleteModalProps {
  campaign: Campaign;
  isDeleteModalOpen: boolean;
  closeDeleteModal: () => void;
}

function CampaignDeleteModal({
  campaign,
  isDeleteModalOpen,
  closeDeleteModal,
}: Readonly<ICampaignDeleteModalProps>): JSX.Element {
  const rootStore = useStore();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();

  const state = campaign.get('state');
  const isNotDraft = state !== CampaignState.Draft;

  const flightOrProject = campaign.get('flight') ? t('flight') : t('project');
  const flightOrProjectURL = campaign.get('flight')
    ? localUrl(campaign.getItemUrl())
    : localUrl(`/projects/${campaign.get('parent')}/report`);

  const alertPageLink = (
    <a href={flightOrProjectURL} target={'_blank'}>
      {t('View {{flightOrProject}} report', { flightOrProject })}
    </a>
  );

  async function getFlight(): Promise<Flight> {
    const { Flights } = rootStore;
    const flight = Flights.get(campaign.get('flight')!);
    await when(() => !flight.isPending);
    return Flights.get(flight.id);
  }

  async function destroy(): Promise<void> {
    try {
      if (campaign.get('flight')) {
        const flight = await getFlight();
        flight.removeCampaign(campaign.id);
        await flight.save();
        // save()/delete() resolve even on a non-2xx response — they flag
        // isErrored instead of rejecting — so surface the failure before
        // reporting success.
        if (flight.isErrored) {
          throw flight.error;
        }
      }
      campaign.set({ is_archived: true });
      await campaign.delete();
      if (campaign.isErrored) {
        throw campaign.error;
      }

      toast(t('Campaign "{{- name}}" has been deleted.', { name: campaign.name }));
      closeDeleteModal();
    } catch (error) {
      toast(
        t('Encountered an error while deleting:\n{{- message}}', {
          message: errorMessage(error, t),
        }),
        {
          type: 'error',
        },
      );
    }
  }

  return (
    <ArchiveModal
      alertDescription={
        isNotDraft
          ? t(
              'Deleting this campaign will make this {{flightOrProject}}\'s reported conversions inaccurate. To recalculate conversions to exclude the deleted campaign, select "Recalculate conversions" from the conversions table on this {{flightOrProject}}\'s report.',
              { flightOrProject },
            )
          : undefined
      }
      alertLinks={isNotDraft && alertPageLink}
      alertTitle={
        isNotDraft &&
        t(
          "This will permanently delete the campaign and all associated data. You can't undo this action.",
        )
      }
      confirmationPhrase={t('DELETE')}
      method={'delete'}
      onClose={closeDeleteModal}
      onConfirm={destroy}
      opened={isDeleteModalOpen}
      requireConfirmation={true}
      t={t}
      title={t('Delete this campaign?')}
    >
      <CampaignSummary campaign={campaign} showMinimal={true} />
    </ArchiveModal>
  );
}

export default CampaignDeleteModal;
